// The v2 shell: top bar (Save + zoom + sign-out), the widget palette, and the
// freeform canvas. Loads the saved board once on mount and hydrates the store;
// Save writes it back. Owns the palette drag (ghost → drop → addWidget) because
// it holds both the canvas content ref and the sticky default player.

import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PlayerRef, PlayerCurrentLeague } from "@catbento/shared";
import { apiGet } from "../api.js";
import { applyCurrentLeague } from "./playerRef.js";
import { useAuth } from "../contexts/AuthContext.js";
import { theme } from "./theme.js";
import { useCanvasStore, ZOOM_SCALES } from "./store.js";
import { toCanvasWidgets } from "./serialize.js";
import { resolveDropPosition, snapToCell, hitTestWidget, placementFits } from "./geometry.js";
import { resolveDrop, mergePlayers } from "./dropResolution.js";
import { assignColors } from "./compareColors.js";
import { DEFAULT_PLAYER } from "./defaults.js";
import { useCanvasBoard, saveCanvas } from "./data/canvas.js";
import { getWidgetDef, type WidgetDef } from "./registry.js";
import Palette from "./components/Palette.js";
import Canvas from "./components/Canvas.js";
import ZoomBar from "./components/ZoomBar.js";

interface PaletteDrag {
  def: WidgetDef;
  x: number;
  y: number;
  /** Would the widget land here? Ghost is green when true, red when false. */
  fits: boolean;
}

/** Drop tone: blue = will merge (combine into a comparison), green = a valid
 *  place/replace, red = can't drop / would be a no-op. */
type DropTone = "merge" | "ok" | "bad";

interface PlayerDrag {
  ref: PlayerRef;
  x: number;
  y: number;
  tone: DropTone;
}

/** What a hover resolves to: which widget to glow (+ how), and the ghost tone. */
interface HoverResult {
  target: { instanceId: string; state: DropTone } | null;
  ghostTone: DropTone;
}

/** Ignore a sub-threshold pointer move so a plain click on a player name doesn't
 *  start a drag (the prototype's 3px threshold — we use 4). */
const DRAG_THRESHOLD = 4;

export default function V2App() {
  const { user, signOut } = useAuth();
  const qc = useQueryClient();
  const contentRef = useRef<HTMLDivElement | null>(null);

  const instances = useCanvasStore((s) => s.instances);
  const zoomIdx = useCanvasStore((s) => s.zoomIdx);
  const dirty = useCanvasStore((s) => s.dirty);
  const loaded = useCanvasStore((s) => s.loaded);
  const scale = ZOOM_SCALES[zoomIdx] ?? 1;

  const boardQ = useCanvasBoard();

  // Hydrate the store once, from the saved board.
  useEffect(() => {
    if (boardQ.data && !loaded) useCanvasStore.getState().hydrate(boardQ.data.widgets);
  }, [boardQ.data, loaded]);

  const saveMut = useMutation({
    mutationFn: () => saveCanvas(toCanvasWidgets(useCanvasStore.getState().instances)),
    onSuccess: (board) => {
      qc.setQueryData(["canvas"], board);
      useCanvasStore.getState().markSaved();
    },
  });

  // ---- palette drag (ghost → drop) -----------------------------------------
  const [paletteDrag, setPaletteDrag] = useState<PaletteDrag | null>(null);
  // ---- player-token drag (drag a player between widgets / onto empty canvas) --
  const [playerDrag, setPlayerDrag] = useState<PlayerDrag | null>(null);
  const [dropTarget, setDropTarget] = useState<{ instanceId: string; state: DropTone } | null>(null);
  // The card that just absorbed a merge — plays a brief blue pop/flash, then clears.
  const [flashId, setFlashId] = useState<string | null>(null);
  const flashMerge = useCallback((id: string) => {
    setFlashId(id);
    setTimeout(() => setFlashId((cur) => (cur === id ? null : cur)), 480);
  }, []);

  // The grid cell a palette drop points at, and whether the widget fits there
  // (freeform, but it must not overlap an existing widget). null = off-canvas.
  function paletteCellFit(def: WidgetDef, clientX: number, clientY: number): { cell: { x: number; y: number }; fits: boolean } | null {
    const point = toCanvasPoint(clientX, clientY);
    if (!point) return null;
    const cell = { x: snapToCell(point.x), y: snapToCell(point.y) };
    const fits = placementFits(cell, def.defaultSize, useCanvasStore.getState().instances.map((i) => i.position));
    return { cell, fits };
  }

  function onTilePointerDown(e: ReactPointerEvent, def: WidgetDef) {
    e.preventDefault();
    setPaletteDrag({ def, x: e.clientX, y: e.clientY, fits: false });

    const onMove = (ev: PointerEvent) => {
      const fit = paletteCellFit(def, ev.clientX, ev.clientY);
      setPaletteDrag({ def, x: ev.clientX, y: ev.clientY, fits: Boolean(fit?.fits) });
    };
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setPaletteDrag(null);
      // Drop only where it fits (green). A spot that overlaps another widget is
      // rejected — nothing happens — rather than sliding the widget elsewhere.
      const fit = paletteCellFit(def, ev.clientX, ev.clientY);
      if (!fit || !fit.fits) return;
      const store = useCanvasStore.getState();
      // Seed a player so a needs-player widget shows data at once: the sticky
      // last-used player, else the built-in default (Rodri).
      const players = def.needsPlayer ? [store.lastPlayer ?? DEFAULT_PLAYER] : [];
      store.addWidget({ widgetKey: def.key, position: { ...fit.cell, w: def.defaultSize.w, h: def.defaultSize.h }, players });
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // Map a client (screen) point to a canvas point (content-origin, un-scaled), or
  // null if it's outside the canvas content. Shared by the hover-hint + the drop.
  function toCanvasPoint(clientX: number, clientY: number): { x: number; y: number } | null {
    const content = contentRef.current;
    if (!content) return null;
    const rect = content.getBoundingClientRect();
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
      return null;
    }
    const sc = ZOOM_SCALES[useCanvasStore.getState().zoomIdx] ?? 1;
    return { x: (clientX - rect.left) / sc, y: (clientY - rect.top) / sc };
  }

  // A player token dragged from a widget (Bio name, a stats column/legend). Drop
  // resolution is the pure module: onto a mergeable widget → add a column; onto a
  // replace-on-drop widget → swap its player; onto empty canvas → a new Bio. The
  // source is never mutated (a drag copies the player, it doesn't move it).
  const onPlayerDragStart = useCallback((e: ReactPointerEvent, ref: PlayerRef) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    let started = false;

    // Resolve the current hover: which widget to glow (+ ok/bad), and the ghost
    // colour. Green = a valid drop (empty canvas → Bio, or a merge/replace that
    // will actually happen); red = a no-op (duplicate/full merge, or a widget that
    // rejects drops like Bio), or off-canvas.
    const resolveHover = (clientX: number, clientY: number): HoverResult => {
      const point = toCanvasPoint(clientX, clientY);
      if (!point) return { target: null, ghostTone: "bad" }; // off-canvas → can't drop
      const insts = useCanvasStore.getState().instances;
      const id = hitTestWidget(insts, point);
      if (!id) return { target: null, ghostTone: "ok" }; // empty canvas → create-bio (green)
      const inst = insts.find((i) => i.instanceId === id);
      const def = inst ? getWidgetDef(inst.widgetKey) : null;
      if (!inst || !def) return { target: null, ghostTone: "bad" };
      if (def.mergeable) {
        const ok = mergePlayers(inst.players, ref, def.maxPlayers).added; // false = dup/full
        // A real merge is electric BLUE (combine into a comparison), not green.
        return ok
          ? { target: { instanceId: id, state: "merge" }, ghostTone: "merge" }
          : { target: { instanceId: id, state: "bad" }, ghostTone: "bad" };
      }
      if (def.replaceOnDrop) return { target: { instanceId: id, state: "ok" }, ghostTone: "ok" };
      return { target: null, ghostTone: "bad" }; // e.g. Bio — rejects drops
    };

    const onMove = (ev: PointerEvent) => {
      if (!started && Math.abs(ev.clientX - startX) < DRAG_THRESHOLD && Math.abs(ev.clientY - startY) < DRAG_THRESHOLD) {
        return; // sub-threshold — still a click, not a drag
      }
      started = true;
      const hov = resolveHover(ev.clientX, ev.clientY);
      setPlayerDrag({ ref, x: ev.clientX, y: ev.clientY, tone: hov.ghostTone });
      setDropTarget(hov.target);
    };

    const onUp = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setPlayerDrag(null);
      setDropTarget(null);
      if (!started) return; // a click, not a drag — nothing to drop

      const point = toCanvasPoint(ev.clientX, ev.clientY);
      if (!point) return; // released off-canvas

      const store = useCanvasStore.getState();
      const targetId = hitTestWidget(store.instances, point);
      const target = targetId ? store.instances.find((i) => i.instanceId === targetId) : null;
      const def = target ? getWidgetDef(target.widgetKey) : null;
      // A hit on a widget with no registry def is inert; only empty canvas creates a Bio.
      if (target && !def) return;
      const action = resolveDrop(def ? { mergeable: def.mergeable, replaceOnDrop: def.replaceOnDrop } : null);

      if (action === "merge" && target && def) {
        const { players, added } = mergePlayers(target.players, ref, def.maxPlayers);
        if (!added) return; // dedup or full — no churn
        const prevColors = (target.state.colors as Record<string, number>) ?? {};
        store.updateWidget(target.instanceId, {
          players,
          state: { ...target.state, colors: assignColors(players, prevColors) },
        });
        flashMerge(target.instanceId);
      } else if (action === "replace" && target) {
        // Single-player after a replace → colours no longer apply.
        store.updateWidget(target.instanceId, { players: [ref], state: { ...target.state, colors: {} } });
      } else if (action === "create-bio") {
        const bioDef = getWidgetDef("player-bio");
        if (!bioDef) return;
        const cell = { x: snapToCell(point.x), y: snapToCell(point.y) };
        const position = resolveDropPosition(
          cell,
          bioDef.defaultSize,
          store.instances.map((i) => i.position)
        );
        store.addWidget({ widgetKey: "player-bio", position, players: [ref] });
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [flashMerge]);

  // Drop a whole single-player card onto another card. Same verbs as the
  // player-token drop: a mergeable target ADDS the player as a comparison column;
  // a replace-on-drop target (Matches) SWAPS its player. The source card is
  // untouched (copy, not move — Canvas returns it to its origin).
  const onCardDrop = useCallback((sourceId: string, targetId: string) => {
    const store = useCanvasStore.getState();
    const source = store.instances.find((i) => i.instanceId === sourceId);
    const target = store.instances.find((i) => i.instanceId === targetId);
    const ref = source?.players[0];
    const def = target ? getWidgetDef(target.widgetKey) : null;
    if (!source || !target || !ref || !def) return;
    const action = resolveDrop({ mergeable: def.mergeable, replaceOnDrop: def.replaceOnDrop });
    if (action === "merge") {
      const { players, added } = mergePlayers(target.players, ref, def.maxPlayers);
      if (!added) return;
      const prevColors = (target.state.colors as Record<string, number>) ?? {};
      store.updateWidget(target.instanceId, {
        players,
        state: { ...target.state, colors: assignColors(players, prevColors) },
      });
      flashMerge(target.instanceId);
    } else if (action === "replace") {
      store.updateWidget(target.instanceId, { players: [ref], state: { ...target.state, colors: {} } });
    }
  }, [flashMerge]);

  const onSignOut = async () => {
    await signOut();
    useCanvasStore.getState().reset();
    qc.clear();
  };

  return (
    <div
      className="v2-scope"
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: theme.page,
        fontFamily: theme.fontBody,
        color: theme.ink,
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: "8px 14px",
          background: theme.tileHeader,
          borderBottom: `1px solid ${theme.border}`,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            color: theme.ink,
            fontFamily: theme.fontDisplay,
            fontWeight: 600,
            fontSize: 20,
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          Catalyst
        </span>
        <span style={{ color: theme.inkFaint, fontSize: 11 }}>v2</span>
        <div style={{ flex: 1 }} />
        {/* The prototype's preset-btn group (outlined pills + right divider). */}
        <div className="preset-group">
          <button className="preset-btn" onClick={() => qc.invalidateQueries()} title="Reload live data">
            <Icon name="refresh" /> Refresh
          </button>
          <button className="preset-btn undo" onClick={() => useCanvasStore.getState().undo()}>
            <Icon name="undo" /> Undo
          </button>
          <button
            className="preset-btn clear"
            onClick={() => useCanvasStore.getState().clearCanvas()}
            title="Remove all widgets (Undo restores them)"
          >
            <Icon name="clear" /> Clear
          </button>
          <button
            className={`preset-btn${dirty && !saveMut.isPending ? " save-dirty" : ""}`}
            onClick={() => saveMut.mutate()}
            disabled={!dirty || saveMut.isPending}
          >
            <Icon name="save" /> {saveMut.isPending ? "Saving…" : dirty ? "Save" : "Saved"}
          </button>
        </div>
        <span style={{ color: theme.inkFaint, fontSize: 11 }}>{user?.email}</span>
        <button className="preset-btn" onClick={onSignOut}>
          <Icon name="signout" /> Sign out
        </button>
      </div>

      {/* Body: palette + canvas */}
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <Palette onTilePointerDown={onTilePointerDown} />
        {boardQ.isLoading ? (
          <div style={{ flex: 1, display: "grid", placeItems: "center", color: theme.inkMuted }}>
            Loading your canvas…
          </div>
        ) : (
          <Canvas
            instances={instances}
            scale={scale}
            contentRef={contentRef}
            onMove={(id, pos) => useCanvasStore.getState().moveWidget(id, pos)}
            onResize={(id, pos) => useCanvasStore.getState().resizeWidget(id, pos)}
            onRemove={(id) => useCanvasStore.getState().removeWidget(id)}
            onPickPlayer={(id, ref) => {
              // Respond immediately with the search default, then upgrade to the
              // resolved CURRENT domestic league in the background — the search
              // "most recent" competition can be a cup or a national-team game
              // (C2b). Only re-set if the widget still holds this same lone
              // player and the league actually changed; a resolver failure just
              // keeps the search default (it's a valid fallback).
              useCanvasStore.getState().setWidgetPlayers(id, [ref]);
              apiGet<PlayerCurrentLeague>(`/player-current-league?playerId=${ref.playerId}`)
                .then((league) => {
                  const upgraded = applyCurrentLeague(ref, league);
                  if (upgraded === ref) return;
                  const w = useCanvasStore
                    .getState()
                    .instances.find((x) => x.instanceId === id);
                  if (w && w.players.length === 1 && w.players[0]?.playerId === ref.playerId) {
                    useCanvasStore.getState().setWidgetPlayers(id, [upgraded]);
                  }
                })
                .catch((e) =>
                  console.error("current-league resolve failed; keeping search default", e)
                );
            }}
            onPlayerDragStart={onPlayerDragStart}
            onCardDrop={onCardDrop}
            dropTarget={dropTarget}
            flashId={flashId}
          />
        )}
      </div>

      {/* Zoom control — fixed on the right (prototype's #zoom-widget). */}
      <ZoomBar zoomIdx={zoomIdx} onZoom={(idx) => useCanvasStore.getState().setZoom(idx)} />

      {/* Palette drag ghost */}
      {paletteDrag ? (
        <div
          style={{
            position: "fixed",
            left: paletteDrag.x + 8,
            top: paletteDrag.y + 8,
            pointerEvents: "none",
            zIndex: 1000,
            padding: "8px 12px",
            background: theme.surface,
            // Green = it will drop here; red = overlaps a widget, drop rejected.
            border: `1px solid ${paletteDrag.fits ? theme.accent : theme.danger}`,
            borderRadius: 8,
            color: theme.ink,
            fontSize: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          }}
        >
          {paletteDrag.def.title}
        </div>
      ) : null}

      {/* Player-token drag ghost */}
      {playerDrag ? (
        <div
          style={{
            position: "fixed",
            left: playerDrag.x + 8,
            top: playerDrag.y + 8,
            pointerEvents: "none",
            zIndex: 1000,
            padding: "5px 10px",
            background: theme.surface,
            // Blue = will merge; green = a valid place/create; red = a no-op.
            border: `1px solid ${
              playerDrag.tone === "merge" ? theme.merge : playerDrag.tone === "ok" ? theme.accent : theme.danger
            }`,
            borderRadius: 999,
            color: theme.ink,
            fontSize: 12,
            fontWeight: 600,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          }}
        >
          {playerDrag.ref.displayName}
        </div>
      ) : null}

      {saveMut.isError ? (
        <div
          style={{
            position: "fixed",
            bottom: 16,
            right: 16,
            padding: "8px 12px",
            background: theme.tile,
            border: `1px solid ${theme.danger}`,
            borderRadius: 8,
            color: theme.danger,
            fontSize: 12,
          }}
        >
          Save failed — try again.
        </div>
      ) : null}
    </div>
  );
}

// Top-bar button glyphs — the prototype's own 12px Feather-style icons
// (css/styles.css / index.html). Styling (pill + hover colours) is the
// .preset-btn CSS in index.html.
const ICON_PATHS: Record<string, React.ReactNode> = {
  refresh: (
    <>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </>
  ),
  undo: (
    <>
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 .49-3.5" />
    </>
  ),
  clear: (
    <>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </>
  ),
  save: (
    <>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </>
  ),
  signout: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
};

function Icon({ name }: { name: keyof typeof ICON_PATHS }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}
